// In Results.tsx

import React, { useMemo, useState, useEffect } from 'react';
import { IResultsProps } from './IResults';
// import { useSelector } from 'react-redux'; // useAppSelector is preferred
import { Grid, Typography, Alert, AlertTitle } from '@mui/material';
import { RootState } from '../../../../reduxService'; // Adatta
import { useTranslation } from 'react-i18next';
import TireAndLayers, { ILayerData, IGeogridInfo, getDrawnLayerStackThickness } from './TireAndLayers'; // Importa Tipi
import { unit as unitEnum, EProjectType } from '../../../enum/project'; // Adatta, Import EProjectType
import { getContainerYardResultInvalidity } from '../../utils/containerYardResultValidity';
// import { selectDynamicProject } from '../../../Dynamic/store/project/projectslice'; // No longer needed directly if using dynamicProject from RootState
import LineChartComponent from './LineChartComponent';
import CustomTable from '../../../components/Layout/CustomTable/CustomTable';
import { getActionByProjectType } from '../../../actionMap';
import { useParams } from 'react-router-dom';
// import { selectProjectInfo } from '../../../store/projectInfo/projectInfoSlice'; // No longer needed if projectInfo comes from dynamicProject
import { useAppSelector } from '../../../../reduxService/hooks';
import { conversionLabel } from '../../../conversion';
import './Results.scss';
import { logDebug, logWarn, logError, LogTag } from '../../../utils/utils';
// Use IContainerYardData as suggested by error, and ensure IDynamicProjectOutputData is correctly defined in IApiResponse.ts
import { IContainerYardData, IDynamicProjectOutputData, IBCRData, IDeckAnalysisDataItem, IParkingDeckData, ILoadDataWheel, IStaticLoadData } from '../../../api/IApiResponse'; // Added IParkingDeckData, ILoadDataWheel, IStaticLoadData
import { convertFieldValue } from '../../../services/UnitConversionService'; // Import for chart default values only

interface Point {
    x: number;
    y: number;
}

// eslint-disable-next-line @typescript-eslint/no-unused-vars
// Helper function moved outside component to avoid React hooks dependencies warnings
const getMeasurementUnit = (unit: string[], unitSystem = 'Metric') => {
    return unit[unitSystem === 'Metric' ? 0 : 1];
};
// eslint-disable-next-line @typescript-eslint/no-unused-vars
const Results = (_props: IResultsProps) => {
    const rawOutput = useAppSelector((state: RootState) => state.Dynamic.results.data?.outputData as IDynamicProjectOutputData | undefined);
    // const projectInfo = useAppSelector(selectProjectInfo); // Sourced from dynamicProject now
    const dynamicProject = useAppSelector((state: RootState) => state.Dynamic.project); // Get the whole dynamicProject state
    const { projectType: routeProjectType } = useParams(); // Renamed to avoid conflict
    const { t } = useTranslation();

    const [unreinforcedStageHeight, setUnreinforcedStageHeight] = useState<number | undefined>(undefined);
    const [unreinforcedSubgradeY, setUnreinforcedSubgradeY] = useState<number | undefined>(undefined);

    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const { graphic } = getActionByProjectType((routeProjectType as EProjectType) || EProjectType.container_yard);
    const [unreinforcedDeckTable, setUnreinforcedDeckTable] = useState<Point[]>([]);
    const [reinforcedDeckTable, setReinforcedDeckTable] = useState<Point[]>([]);
    // const [bcrDeckTable, setBcrDeckTable] = useState<Point[]>([]); // Removed as BCR curve is no longer displayed

    const currentProjectType = dynamicProject.data?.type; // Get project type
    //get project method
    const currentProjectMethod = dynamicProject.data?.method; // Get project method
    logDebug('CYResults', 'Current project type:', currentProjectType, LogTag.PROJECT_INFO);
    logDebug('CYResults', 'Current project method:', currentProjectMethod, LogTag.PROJECT_INFO);

    // 🆕 SIMPLIFIED APPROACH: Redux contains data already in the correct unit
    // No need for conversion - the data from Redux is already in the display unit
    const processedOutput = useMemo(() => {
        logDebug('CYResults', 'Using output data directly from API - conversion handled at Redux level', undefined, LogTag.UNIT_CONVERSION);

        // 🔧 FIX: Convert deckDesign results when unit is Imperial
        if (rawOutput && dynamicProject.data?.unit === unitEnum.Imperial && rawOutput.deckDesign) {
            const convertedOutput = { ...rawOutput };

            // Convert unreinforcedHeight and reinforcedHeight from metric to Imperial
            if (typeof rawOutput.deckDesign.unreinforcedHeight === 'number') {
                convertedOutput.deckDesign = {
                    ...rawOutput.deckDesign,
                    unreinforcedHeight: Number((rawOutput.deckDesign.unreinforcedHeight * 3.28084).toFixed(2)), // m to ft, rounded
                    reinforcedHeight:
                        typeof rawOutput.deckDesign.reinforcedHeight === 'number'
                            ? Number((rawOutput.deckDesign.reinforcedHeight * 3.28084).toFixed(2)) // m to ft, rounded
                            : rawOutput.deckDesign.reinforcedHeight,
                };

                logDebug(
                    'CYResults',
                    'Converted deckDesign results to Imperial:',
                    {
                        originalUnreinforced: rawOutput.deckDesign.unreinforcedHeight,
                        convertedUnreinforced: convertedOutput.deckDesign.unreinforcedHeight,
                        originalReinforced: rawOutput.deckDesign.reinforcedHeight,
                        convertedReinforced: convertedOutput.deckDesign.reinforcedHeight,
                    },
                    LogTag.UNIT_CONVERSION
                );
            }

            return convertedOutput;
        }

        return rawOutput;
    }, [rawOutput, dynamicProject.data?.unit]);

    // Read on the SI values: processedOutput converts Imperial projects to ft
    const resultInvalidity = useMemo(() => {
        const invalidity = getContainerYardResultInvalidity({
            projectType: currentProjectType,
            method: currentProjectMethod,
            unreinforcedHeight: rawOutput?.deckDesign?.unreinforcedHeight,
            reinforcedHeight: rawOutput?.deckDesign?.reinforcedHeight,
        });
        if (invalidity) logWarn('CYResults', `Invalid result: ${invalidity}`, rawOutput?.deckDesign, LogTag.VALIDATION);
        return invalidity;
    }, [currentProjectType, currentProjectMethod, rawOutput?.deckDesign]);
    const isInvalidResult = resultInvalidity !== null;

    logDebug('CYResults', 'Output for parking deck check:', processedOutput?.deckDesign, LogTag.DATA_TRANSFORM);

    // Source inputs from dynamicProject.data.projectInfo.data
    const projectInputs = useMemo(() => {
        if (dynamicProject.data?.type === EProjectType.container_yard) {
            return dynamicProject.data.projectInfo?.data as IContainerYardData | undefined;
        } else if (dynamicProject.data?.type === EProjectType.parking_deck) {
            return dynamicProject.data.projectInfo?.data as IParkingDeckData | undefined;
        }
        return undefined;
    }, [dynamicProject.data]);

    logDebug('CYResults', 'Output data:', processedOutput, LogTag.DATA_TRANSFORM);
    logDebug('CYResults', 'Project inputs from dynamicProject:', projectInputs, LogTag.DATA_TRANSFORM);

    const loadResults = React.useMemo(() => {
        if (!projectInputs) return [];

        // 🔧 FIX: Access FLAT structure directly (PD3/PD4)
        // Data is at top level after transformProjectFromBackend()
        if (currentProjectMethod !== 'bcr') return [];

        if (currentProjectType === EProjectType.container_yard) {
            const cyBcrLoad = (projectInputs as any)?.load;
            return [
                [t('deckWidth'), 'B', cyBcrLoad?.deckWidth?.toString() || '', getMeasurementUnit(conversionLabel.m_ft, dynamicProject.data?.unit)],
                [t('deckLength'), 'L', cyBcrLoad?.deckLength?.toString() || '', getMeasurementUnit(conversionLabel.m_ft, dynamicProject.data?.unit)],
                [t('uniformLoadRes'), 'q', cyBcrLoad?.uniformLoad?.toString() || '', getMeasurementUnit(conversionLabel.kPa, dynamicProject.data?.unit)],
                [t('safetyFactor'), 'SF', (projectInputs as any)?.fsDesign?.fsDesign?.toString() || '', '-'],
            ];
        } else if (currentProjectType === EProjectType.parking_deck) {
            const pdBcrLoad = (projectInputs as any).load as ILoadDataWheel; // Type assertion
            return [
                [t('wheel_load', 'Wheel Load'), 'P', pdBcrLoad?.wheelLoad?.toString() || '', getMeasurementUnit(conversionLabel.kN_lbf, dynamicProject.data?.unit)],
                [t('tire_inflation_pressure', 'Tire Inflation Pressure'), 'p', pdBcrLoad?.tireInflationPressure?.toString() || '', getMeasurementUnit(conversionLabel.kPa, dynamicProject.data?.unit)],
                [t('safetyFactor'), 'SF', (projectInputs as any).fsDesign?.fsDesign?.toString() || '', '-'],
            ];
        }
        return [];
    }, [projectInputs, currentProjectType, currentProjectMethod, t, dynamicProject.data?.unit]);

    const subgradeResults = React.useMemo(() => {
        if (!projectInputs) return [];

        // 🔧 FIX: Access FLAT structure directly (PD3/PD4)
        if (currentProjectMethod !== 'bcr') return [];

        // Access subgrade data using the correct runtime key 'subgradeCharacteristics'
        const bcrSubgrade = (projectInputs as any).subgradeCharacteristics;

        // Log to verify the data being accessed
        logDebug('CYResults', 'subgradeResults projectInputs:', projectInputs, LogTag.CALCULATIONS);
        logDebug('CYResults', 'subgradeResults bcrSubgrade:', bcrSubgrade, LogTag.CALCULATIONS);

        return [
            [t('unitWeightRes'), 'γ', bcrSubgrade?.unitWeight?.toString() || '', getMeasurementUnit(conversionLabel.kNm3_lbfft3, dynamicProject.data?.unit)],
            [t('frictionAngleRes'), 'φ', bcrSubgrade?.frictionAngle?.toString() || '', getMeasurementUnit(conversionLabel.deg, dynamicProject.data?.unit)],
            [t('cohesionRes'), 'c', bcrSubgrade?.cohesion?.toString() || '', getMeasurementUnit(conversionLabel.kPa, dynamicProject.data?.unit)],
        ];
    }, [projectInputs, currentProjectMethod, t, dynamicProject.data?.unit]);

    const embarkmentResults = React.useMemo(() => {
        if (!projectInputs) return [];

        // 🔧 FIX: Access FLAT structure directly (PD3/PD4)
        if (currentProjectMethod !== 'bcr') return [];

        const bcrEmbarkment = (projectInputs as any).embankmentCharacteristics;
        return [
            [t('embarkment_weight', { unit_measure: '' }).trim(), 'γ', bcrEmbarkment?.unitWeight?.toString() || '', getMeasurementUnit(conversionLabel.kNm3_lbfft3, dynamicProject.data?.unit)],
            [t('embarkment_friction_angle', { unit_measure: '' }).trim(), 'φ', bcrEmbarkment?.frictionAngle?.toString() || '', getMeasurementUnit(conversionLabel.deg, dynamicProject.data?.unit)],
            [t('embarkment_cohesion', { unit_measure: '' }).trim(), 'c', bcrEmbarkment?.cohesion?.toString() || '', getMeasurementUnit(conversionLabel.kPa, dynamicProject.data?.unit)],
            [t('loadSpreadingAngleRes'), 'αᵤ', bcrEmbarkment?.loadSpreadingAngleU?.toString() || '', getMeasurementUnit(conversionLabel.deg, dynamicProject.data?.unit)],
            [t('loadSpreadingAngleRes'), 'αᵣ', bcrEmbarkment?.loadSpreadingAngleR?.toString() || '', getMeasurementUnit(conversionLabel.deg, dynamicProject.data?.unit)],
        ];
    }, [projectInputs, currentProjectMethod, t, dynamicProject.data?.unit]);

    // --- STATIC method input data (separate tables for Load and Subgrade like BCR) ---
    const staticLoadResults = React.useMemo(() => {
        if (!projectInputs) return [];

        // 🔧 FIX: Access FLAT structure directly (PD3/PD4)
        if (currentProjectMethod !== 'static') return [];

        let loadRows: string[][] = [];
        if (currentProjectType === EProjectType.container_yard) {
            const cyStaticLoad = (projectInputs as any).load as IStaticLoadData;
            loadRows = [
                [String(t('static_deck_half_width', 'Deck Half Width')), "B'", cyStaticLoad?.deckHalfWidth?.toString() || '', getMeasurementUnit(conversionLabel.m_ft, dynamicProject.data?.unit)],
                [String(t('static_deck_half_length', 'Deck Half Length')), "L'", cyStaticLoad?.deckHalfLength?.toString() || '', getMeasurementUnit(conversionLabel.m_ft, dynamicProject.data?.unit)],
                [String(t('static_uniform_load', 'Uniform Load')), 'q', cyStaticLoad?.uniformLoad?.toString() || '', getMeasurementUnit(conversionLabel.kPa, dynamicProject.data?.unit)],
                [String(t('static_safety_factor', 'Safety Factor')), 'FS', (projectInputs as any).fsDesign?.fsDesign?.toString() || '', '-'],
            ];
        } else if (currentProjectType === EProjectType.parking_deck) {
            const pdStaticLoad = (projectInputs as any).load as ILoadDataWheel;
            loadRows = [
                [String(t('wheel_load', 'Wheel Load')), 'P', pdStaticLoad?.wheelLoad?.toString() || '', getMeasurementUnit(conversionLabel.kN_lbf, dynamicProject.data?.unit)],
                [
                    String(t('tire_inflation_pressure', 'Tire Inflation Pressure')),
                    'p',
                    pdStaticLoad?.tireInflationPressure?.toString() || '',
                    getMeasurementUnit(conversionLabel.kPa, dynamicProject.data?.unit),
                ],
                [String(t('static_safety_factor', 'Safety Factor')), 'FS', (projectInputs as any).fsDesign?.fsDesign?.toString() || '', '-'],
            ];
        }

        return loadRows;
    }, [projectInputs, currentProjectType, currentProjectMethod, t, dynamicProject.data?.unit]);

    const staticSubgradeResults = React.useMemo(() => {
        if (!projectInputs) return [];

        // 🔧 FIX: Access FLAT structure directly (PD3/PD4)
        if (currentProjectMethod !== 'static') return [];

        const subgradeRows: string[][] = [];
        const staticSubgrade = (projectInputs as any).subgrade;
        if (staticSubgrade?.cbrSubgrade !== undefined) {
            subgradeRows.push([String(t('static_cbr', 'CBR')), 'CBR', staticSubgrade.cbrSubgrade.toString(), '%']);
        } else if (staticSubgrade?.deformationModulus !== undefined) {
            subgradeRows.push([String(t('static_deformation_modulus', 'Deformation Modulus')), 'E', staticSubgrade.deformationModulus.toString(), 'MPa']);
        }

        return subgradeRows;
    }, [projectInputs, currentProjectMethod, t]);

    // --- Prepara i dati per il diagramma NON RINFORZATO (Sinistra) ---
    const unreinforcedGraphicData = useMemo(() => {
        if (!processedOutput?.deckDesign || !dynamicProject.data) return null;

        const unitLabel = dynamicProject.data?.unit === unitEnum.Imperial ? t('ft_unit', 'ft') : t('m_unit', 'm');
        const hu = processedOutput.deckDesign.unreinforcedHeight; // Always use unreinforcedHeight

        if (hu === undefined) {
            logWarn('CYResults', 'Final unreinforcedHeight (hu) is undefined. Diagram will not render.', undefined, LogTag.VALIDATION);
            return null;
        }

        const surfaceThick = 0.0; // <-- modificato da 0.1 a 0.0
        const baseThickUnreinforced = Math.max(0, hu - surfaceThick);
        const subgradeVisible = 0.5; // Altezza fissa disegno

        const surfaceLayer: ILayerData = { name: t('surface_layer'), thickness: surfaceThick, color: '#5A5A5A' };
        const baseCourse: ILayerData = { name: t('base_course'), thickness: baseThickUnreinforced, color: '#D3D3D3' };
        const subgrade: ILayerData = { name: t('subgrade'), thickness: subgradeVisible, color: '#DEB887' };

        return { surfaceLayer, baseCourse, subgrade, rightDimensionValue: hu, unitLabel };
    }, [dynamicProject, processedOutput, t]);

    // --- Prepara i dati per il diagramma RINFORZATO (Destra) ---
    const reinforcedGraphicData = useMemo(() => {
        if (!processedOutput?.deckDesign || !dynamicProject.data) return null;

        const unitLabel = dynamicProject.data?.unit === unitEnum.Imperial ? t('ft_unit', 'ft') : t('m_unit', 'm');
        const hr = processedOutput.deckDesign.reinforcedHeight; // Always use reinforcedHeight
        const currentSavingPercentage = processedOutput.deckDesign.thicknessSavingPercentage; // Always use thicknessSavingPercentage

        if (hr === undefined) {
            logWarn('CYResults', 'Final reinforcedHeight (hr) is undefined. Diagram will not render.', undefined, LogTag.VALIDATION);
            return null;
        }

        const surfaceThick = 0.0; // <-- modificato da 0.1 a 0.0
        const baseThickReinforced = Math.max(0, hr - surfaceThick); // Base course is total reinforced height minus surface
        const subgradeVisible = 0.5;

        const firstBaseGeogrid = dynamicProject.data?.projectInfo?.data?.geoGrid?.geogridBaseCourse?.geogrids?.[0];

        // Debug code removed - was unused

        // Create geogrid info: use existing data if available, otherwise create default geogrid for all projects
        const geogridInfo: IGeogridInfo | undefined = firstBaseGeogrid
            ? {
                  // Override position to place geogrid closer to subgrade interface regardless of saved data
                  positionFromBottomOfBase: dynamicProject.data?.unit === unitEnum.Imperial ? 0.01 : 0.003,
                  label: firstBaseGeogrid.selectedGeogrid,
              }
            : {
                  // Default geogrid for all projects - positioned just above the subgrade interface
                  // positionFromBottomOfBase represents distance from bottom of base course (subgrade interface)
                  // Use very small values to position geogrid right at the subgrade boundary
                  positionFromBottomOfBase: dynamicProject.data?.unit === unitEnum.Imperial ? 0.01 : 0.003, // Very close: 0.01 ft or 0.3 cm from subgrade interface
                  label: 'GEOGRID',
                  color: '#24244f',
              };

        const surfaceLayer: ILayerData = { name: t('surface_layer'), thickness: surfaceThick, color: '#5A5A5A' };
        const baseCourse: ILayerData = { name: t('base_course'), thickness: baseThickReinforced, color: '#D3D3D3' };
        const subgrade: ILayerData = { name: t('subgrade'), thickness: subgradeVisible, color: '#DEB887' };

        return { surfaceLayer, baseCourse, subgrade, rightDimensionValue: hr, savingPercentage: currentSavingPercentage, geogrid: geogridInfo, unitLabel };
    }, [dynamicProject, processedOutput, t]);

    // Both diagrams scale on the taller stack, so the unstabilised and stabilised thicknesses stay visually comparable
    const diagramScaleReferenceThickness = Math.max(
        unreinforcedGraphicData ? getDrawnLayerStackThickness(unreinforcedGraphicData.baseCourse, unreinforcedGraphicData.subgrade) : 0,
        reinforcedGraphicData ? getDrawnLayerStackThickness(reinforcedGraphicData.baseCourse, reinforcedGraphicData.subgrade) : 0
    );
    // Note: IDeckAnalysisDataItemWithBcr interface was removed as BCR curve is no longer used

    useEffect(() => {
        const mapDeckAnalysisData = (data: IDeckAnalysisDataItem[] | undefined): Point[] => {
            if (!data) return [];
            try {
                return data.map(item => ({
                    x: item.h,
                    y: item.fs,
                }));
            } catch (error) {
                logError('CYResults', 'Errore durante la mappatura dei dati di analisi del deck:', error, LogTag.ERROR_HANDLING);
                return [];
            }
        };

        if (processedOutput?.deckDesign?.unreinforcedDeckAnalysisData) {
            setUnreinforcedDeckTable(mapDeckAnalysisData(processedOutput.deckDesign.unreinforcedDeckAnalysisData));
        } else {
            setUnreinforcedDeckTable([]);
        }

        if (processedOutput?.deckDesign?.reinforcedDeckAnalysisData) {
            setReinforcedDeckTable(mapDeckAnalysisData(processedOutput.deckDesign.reinforcedDeckAnalysisData));
        } else {
            setReinforcedDeckTable([]);
        }

        // BCR curve data is no longer used as it was removed from the chart
    }, [processedOutput]);

    const deckResults = useMemo(() => {
        if (!processedOutput?.deckDesign) return [];
        // Round thicknessSavingPercentage to integer (no decimals for percentages)
        let roundedSavingPercentage = '';
        if (typeof processedOutput.deckDesign.thicknessSavingPercentage === 'number') {
            roundedSavingPercentage = Math.round(processedOutput.deckDesign.thicknessSavingPercentage).toString();
        } else if (processedOutput.deckDesign.thicknessSavingPercentage != null) {
            roundedSavingPercentage = String(processedOutput.deckDesign.thicknessSavingPercentage);
        }
        return [
            [t('unstabilized_deck', { unit_measure: '' }), 'hᵤ', processedOutput.deckDesign.unreinforcedHeight?.toString() || '', getMeasurementUnit(conversionLabel.m_ft, dynamicProject.data?.unit)],
            [t('stabilized_deck', { unit_measure: '' }), 'hₛ', processedOutput.deckDesign.reinforcedHeight?.toString() || '', getMeasurementUnit(conversionLabel.m_ft, dynamicProject.data?.unit)],
            [t('thickness_saving', { unit_measure: 'm' }), 'Δh', processedOutput.deckDesign.thicknessSaving?.toString() || '', getMeasurementUnit(conversionLabel.m_ft, dynamicProject.data?.unit)],
            [t('thickness_saving_percentage', { unit_measure: 'm' }), 'Δh', roundedSavingPercentage, '%'],
        ];
    }, [processedOutput?.deckDesign, t, dynamicProject.data?.unit]);

    // Note: computedMaxH was removed as we now use fixed xMax of 2.0

    // Compute the maximum FS value from chart data for dynamic Y-axis scaling
    const computedMaxFS = useMemo(() => {
        const allFsValues = [...unreinforcedDeckTable.map(p => p.y), ...reinforcedDeckTable.map(p => p.y)].filter(y => typeof y === 'number' && !isNaN(y));

        if (allFsValues.length === 0) return 9; // fallback

        const maxFs = Math.max(...allFsValues);
        // Round up to nearest integer, minimum 9
        return Math.max(9, Math.ceil(maxFs));
    }, [unreinforcedDeckTable, reinforcedDeckTable]);

    // --- STATIC chart data mapping ---
    const staticChartData = useMemo(() => {
        if (currentProjectMethod === 'static' && processedOutput?.deckDesign && Array.isArray((processedOutput.deckDesign as any).iterationTable)) {
            // Estraggo i dati dalla tabella delle iterazioni
            const iterationTable = (processedOutput.deckDesign as any).iterationTable as {
                height: number;
                stabBearingCapacity: number;
                unstabBearingCapacity: number;
                verticalPressure: number;
            }[];

            if (!iterationTable || iterationTable.length === 0) return null;

            // Mappa i dati per ogni curva. Axes are swapped:
            // X-axis: q (kPa) values
            // Y-axis: H (m) values
            const vertPressure = iterationTable.map(item => ({
                x: item.verticalPressure, // X is now Pressure
                y: item.height, // Y is now Height
            }));
            const unstab = iterationTable.map(item => ({
                x: item.unstabBearingCapacity, // X is now Capacity
                y: item.height, // Y is now Height
            }));
            const stab = iterationTable.map(item => ({
                x: item.stabBearingCapacity, // X is now Capacity
                y: item.height, // Y is now Height
            }));

            return { vertPressure, unstab, stab };
        }
        return null;
    }, [currentProjectMethod, processedOutput]);

    // --- PARKING DECK STATIC chart data mapping (cuKPa vs. H) ---
    const parkingDeckStaticChartData = useMemo(() => {
        // Ensure EProjectType.parking_deck is correctly defined and imported
        if (currentProjectType === EProjectType.parking_deck && currentProjectMethod === 'static' && processedOutput?.deckDesign?.cbrLookupTable) {
            const cbrTable = processedOutput.deckDesign.cbrLookupTable;
            if (!Array.isArray(cbrTable) || cbrTable.length === 0) return null;

            const hUnstabilisedData = cbrTable.map(item => ({
                x: item.cuKPa, // X-axis: cuKPa
                y: -item.hUnstabilisedM, // Y-axis: negative for chart display (domain is [-maxH, 0])
            }));

            const hStabilisedData = cbrTable.map(item => ({
                x: item.cuKPa, // X-axis: cuKPa
                y: -item.hStabilisedM, // Y-axis: negative for chart display (domain is [-maxH, 0])
            }));
            logDebug('CYResults', 'parkingDeckStaticChartData prepared:', { hUnstabilisedData, hStabilisedData }, LogTag.CHARTS);
            return { hUnstabilisedData, hStabilisedData };
        }
        return null;
    }, [currentProjectType, currentProjectMethod, processedOutput]);

    // --- Chart rendering logic ---
    const renderChartSection = () => {
        const isImperial = dynamicProject.data?.unit === unitEnum.Imperial;
        const hLabel = isImperial ? t('h_ft_axis_label', 'H (ft)') : t('h_m_axis_label', 'H (m)');
        const qLabel = isImperial ? t('q_psi_axis_label', 'q (psi)') : t('q_kpa_axis_label', 'q (kPa)');
        const cuLabel = isImperial ? t('cu_psi_axis_label', 'Cu (psi)') : t('cukpa_axis_label', 'Cu (kPa)');

        if (currentProjectType === EProjectType.parking_deck && currentProjectMethod === 'static' && parkingDeckStaticChartData) {
            logDebug('CYResults', 'Rendering Parking Deck Static Chart', undefined, LogTag.CHARTS);
            return (
                <LineChartComponent
                    data={parkingDeckStaticChartData.hStabilisedData} // Maps to legend dataKey 'reinforced'
                    reinforcedData={parkingDeckStaticChartData.hUnstabilisedData} // Maps to legend dataKey 'unreinforced'
                    // bcrData is undefined for this chart type
                    xLabel={cuLabel as string}
                    yLabel={hLabel as string}
                    yMax={isImperial ? (convertFieldValue({ name: 'yMaxParking', type: 'number', unit: ['m', 'ft'] }, 2, unitEnum.SI, unitEnum.Imperial, 'yMaxParking') ?? 2) : 2}
                    xMax={isImperial ? (convertFieldValue({ name: 'xMaxParking', type: 'number', unit: ['kPa', 'psi'] }, 160, unitEnum.SI, unitEnum.Imperial, 'xMaxParking') ?? 160) : 160}
                    legend={[
                        { dataKey: 'unreinforced', color: '#8884d8', name: t('h_unstabilised_m_legend', 'H Unstabilised (m)') as string },
                        { dataKey: 'reinforced', color: '#82ca9d', name: t('h_stabilised_m_legend', 'H Stabilised (m)') as string },
                    ]}
                    method={'static'} // Use 'static' method for H (m) Y-axis and potentially similar X-axis scaling
                />
            );
        } else if (currentProjectMethod === 'static' && staticChartData && staticChartData.vertPressure.length > 0) {
            logDebug('CYResults', 'Rendering Standard Static Chart for Container Yard', undefined, LogTag.CHARTS);
            return (
                <LineChartComponent
                    data={staticChartData.stab} // stab data: { x: stabCapacity, y: height }
                    reinforcedData={staticChartData.unstab} // unstab data: { x: unstabCapacity, y: height }
                    bcrData={staticChartData.vertPressure} // vertPressure data: { x: vertPressure, y: height }
                    // Custom labels for static chart - SWAPPED
                    xLabel={qLabel} // X-axis is now Pressure/Capacity
                    yLabel={hLabel} // Y-axis is now Height
                    legend={[
                        { dataKey: 'unreinforced', color: '#0000ff', name: 'UNSTAB. BEARING CAPACITY qua' },
                        { dataKey: 'reinforced', color: '#ff0000', name: 'STAB. BEARING CAPACITY qra' },
                        { dataKey: 'vertPressure', color: '#000000', name: 'VERT. PRESSURE σv' },
                    ]}
                    method={'static'}
                />
            );
        }
        // Default: grafico classico
        const defaultXMax = isImperial ? convertFieldValue({ name: 'defaultXMax', type: 'number', unit: ['m', 'ft'] }, 2.0, unitEnum.SI, unitEnum.Imperial, 'defaultXMax') : 2.0;
        return (
            <LineChartComponent
                data={unreinforcedDeckTable}
                reinforcedData={reinforcedDeckTable}
                // Remove BCR curve as requested
                // bcrData={bcrDeckTable}
                xLabel={hLabel}
                yLabel={t('fs_axis_label', 'FS') as string}
                xMax={defaultXMax ?? 2.0}
                yMax={computedMaxFS}
                method={'bcr'}
            />
        );
    };

    const renderBcrInputTables = () => (
        <>
            <Grid className="ntk-tabella" container spacing={0} justifyContent="center" alignItems="center">
                <Grid item xs={8}>
                    <Typography className="table-title">{t('load')}</Typography>
                    <CustomTable columns={[t('output_description'), t('output_symbol'), t('output_value'), t('output_unit')]} data={loadResults} />
                </Grid>
            </Grid>
            <Grid className="ntk-tabella" container spacing={0} justifyContent="center" alignItems="center">
                <Grid item xs={8}>
                    <Typography className="table-title">{t('subgrade')}</Typography>
                    <CustomTable columns={[t('output_description'), t('output_symbol'), t('output_value'), t('output_unit')]} data={subgradeResults} />
                </Grid>
            </Grid>
            <Grid className="ntk-tabella" container spacing={0} justifyContent="center" alignItems="center" sx={{ borderBottom: '0 solid' }}>
                <Grid item xs={8}>
                    <Typography className="table-title">{t('embarkment_fill')}</Typography>
                    <CustomTable columns={[t('output_description'), t('output_symbol'), t('output_value'), t('output_unit')]} data={embarkmentResults} />
                </Grid>
                <Grid item xs={5}></Grid>
            </Grid>
        </>
    );

    const renderStaticInputTables = () => (
        <>
            <Grid className="ntk-tabella" container spacing={0} justifyContent="center" alignItems="center">
                <Grid item xs={8}>
                    <Typography className="table-title">{t('load')}</Typography>
                    <CustomTable columns={[t('output_description'), t('output_symbol'), t('output_value'), t('output_unit')]} data={staticLoadResults} />
                </Grid>
            </Grid>
            <Grid className="ntk-tabella" container spacing={0} justifyContent="center" alignItems="center" sx={{ borderBottom: '0 solid' }}>
                <Grid item xs={8}>
                    <Typography className="table-title">{t('subgrade')}</Typography>
                    <CustomTable columns={[t('output_description'), t('output_symbol'), t('output_value'), t('output_unit')]} data={staticSubgradeResults} />
                </Grid>
            </Grid>
        </>
    );

    // Helper function to get appropriate error message
    const getInvalidResultMessage = () => {
        if (resultInvalidity === 'negativeThickness') {
            return {
                title: t('invalid_calculation_result', 'Invalid Calculation Result'),
                message: t(
                    'negative_thickness_error',
                    'The backend returned negative thickness values, which indicates the design parameters do not allow for a valid solution. Please review your input parameters.'
                ),
            };
        }
        if (resultInvalidity === 'bcrGoalSeekWithoutSolution' || resultInvalidity === 'reinforcedThickerThanUnreinforced') {
            return {
                title: t('check_your_input', 'Check your input'),
                message: t('iso_limitation_error', 'The analysis has reached a method limitation defined by ISO 18228-5. Please check your input parameters.'),
            };
        }
        return null;
    };

    // Early return for invalid results
    if (isInvalidResult) {
        const errorMessage = getInvalidResultMessage();
        return (
            <Grid container justifyContent="center" alignItems="center" style={{ minHeight: '400px' }}>
                <Grid item xs={12} md={8} lg={6}>
                    <Alert severity="error" sx={{ p: 3 }}>
                        <AlertTitle>{errorMessage?.title || t('invalid_result', 'Invalid Result')}</AlertTitle>
                        {errorMessage?.message || t('generic_error_message', 'Please check your input parameters.')}
                    </Alert>
                    {/* Show input tables even when invalid */}
                    <Grid container mt={3}>
                        <Grid item xs={12}>
                            {currentProjectMethod === 'bcr' ? renderBcrInputTables() : renderStaticInputTables()}
                        </Grid>
                    </Grid>
                </Grid>
            </Grid>
        );
    }

    return (
        <Grid container id="results-component-container">
            {/* --- Renderizza i DUE diagrammi --- */}
            <Grid item xs={12} md={6} style={{ display: 'flex', flexDirection: 'column' }}>
                <Typography align="center" variant="subtitle1" gutterBottom>
                    {t('unstabilised_title', 'UNSTABILISED')}
                </Typography>
                {/* Aggiunto flexGrow: 1 al div contenitore del diagramma */}
                <div
                    id="tire-layer-unreinforced"
                    className="cont-grafico"
                    style={{ flexGrow: 1, display: 'flex', justifyContent: 'flex-start', alignItems: 'flex-end', border: '0px solid #eee', padding: '10px', minHeight: '300px' }}>
                    {unreinforcedGraphicData ? (
                        <TireAndLayers
                            // Dati layer specifici per Unreinforced
                            surfaceLayer={unreinforcedGraphicData.surfaceLayer}
                            baseCourse={unreinforcedGraphicData.baseCourse}
                            subgrade={unreinforcedGraphicData.subgrade}
                            // Mostra solo quota destra
                            rightDimensionValue={unreinforcedGraphicData.rightDimensionValue}
                            showLeftDimension={false}
                            showRightDimension={true}
                            showSavingBox={false}
                            showGeogrid={false}
                            showTire={true}
                            showLayers={true}
                            hideLayerTitles={false} // mantengo visibili le altre label...
                            hideSurfaceLabel={true} // ...ma nascondo solo la label del surface
                            unitLabel={unreinforcedGraphicData.unitLabel}
                            scaleReferenceThickness={diagramScaleReferenceThickness}
                            projectType={currentProjectType}
                            onStageHeightCalculated={h => {
                                logDebug('CYResults', 'Altezza sinistro:', h, LogTag.CHARTS);
                                setUnreinforcedStageHeight(h);
                            }}
                            onSubgradeYCalculated={y => {
                                logDebug('CYResults', 'SubgradeY sinistro:', y, LogTag.CHARTS);
                                setUnreinforcedSubgradeY(y);
                            }}
                        />
                    ) : (
                        <Typography>Loading diagram...</Typography>
                    )}
                </div>
            </Grid>

            <Grid item xs={12} md={6} style={{ display: 'flex', flexDirection: 'column' }}>
                <Typography align="center" variant="subtitle1" gutterBottom>
                    {t('stabilised_title', 'STABILISED')}
                </Typography>
                {/* Aggiunto flexGrow: 1 al div contenitore del diagramma */}
                <div
                    id="tire-layer-reinforced"
                    className="cont-grafico"
                    style={{ flexGrow: 1, display: 'flex', justifyContent: 'flex-start', alignItems: 'flex-end', border: '0px solid #eee', padding: '10px', minHeight: '300px' }}>
                    {reinforcedGraphicData ? (
                        <TireAndLayers
                            // Dati layer specifici per Reinforced
                            surfaceLayer={reinforcedGraphicData.surfaceLayer}
                            baseCourse={reinforcedGraphicData.baseCourse}
                            subgrade={reinforcedGraphicData.subgrade}
                            rightDimensionValue={reinforcedGraphicData.rightDimensionValue}
                            savingPercentage={reinforcedGraphicData.savingPercentage}
                            geogrid={reinforcedGraphicData.geogrid}
                            showRightDimension={true}
                            showSavingBox={true}
                            showGeogrid={true}
                            showTire={true}
                            showLeftDimension={false}
                            unitLabel={reinforcedGraphicData.unitLabel}
                            customStageHeight={unreinforcedStageHeight}
                            scaleReferenceThickness={diagramScaleReferenceThickness}
                            alignFromBottom={true}
                            targetSubgradeY={unreinforcedSubgradeY}
                            hideLayerTitles={false}
                            hideSurfaceLabel={true}
                            projectType={currentProjectType}
                        />
                    ) : (
                        <Typography>Loading diagram...</Typography>
                    )}
                </div>
            </Grid>
            <Grid className="ntk-tabella" mt={3} container spacing={0} justifyContent="center" alignItems="center" sx={{ borderBottom: '0 solid' }}>
                <Grid item xs={8}>
                    <CustomTable columns={[t('output_description'), t('output_symbol'), t('output_value'), t('output_unit')]} data={deckResults} />
                </Grid>
            </Grid>
            <Grid className="ntk-grafico" container spacing={0} style={{ display: 'flex', justifyContent: 'center', width: '100%' }}>
                <Grid item xs={11} style={{ marginBottom: 30 }} id="line-chart-results">
                    {renderChartSection()}
                </Grid>
            </Grid>
            {/* Check if projectInputs are available to decide rendering these tables */}
            {projectInputs && currentProjectMethod === 'bcr' && renderBcrInputTables()}
            {projectInputs && currentProjectMethod === 'static' && renderStaticInputTables()}
        </Grid>
    );
};

export default Results;
